// LeaderboardsListPage — list of all leaderboards inside a project.
//
// Ports docs/mockups/leaderboards-list.html (6 states: populated, filtered,
// filtered empty, loading skeleton, error, zero-state). Default filter follows
// the mockup: status = "active". An "empty + no filter narrowed" check decides
// between "No leaderboards yet" (zero-state) and "No leaderboards match these
// filters" (filtered empty) — see emptyHeadline below.
//
// Participants column always renders "—" — the backend's per-leaderboard list
// does not include a participants count (it lives on the latest open Cycle and
// is fetched in a later task). The mockup numbers there are visual fillers.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MoreVertical, Search, SearchX, Trophy, X } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Select } from "../../components/Select/Select";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../lib/errors";
import { formatRelativeShort } from "../../lib/format";
import { useProject } from "../projects/useProject";
import { lbKeys, listLeaderboards } from "./api";
import { operatorBadgeFor, windowBadgeFor } from "./boardBadges";
import type {
  Leaderboard,
  LeaderboardStatus,
  ListFilter,
  WindowKind,
} from "./types";
import styles from "./LeaderboardsListPage.module.css";

// === Scope label ===================================================== //

function scopeLabelFor(row: Leaderboard): string {
  if (row.scope === "guild_aggregated") {
    return `guild_aggregated (${row.guild_aggregator ?? "sum"})`;
  }
  return row.scope;
}

// === Component ======================================================= //

const STATUS_OPTIONS: Array<{ value: LeaderboardStatus | ""; label: string }> = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "archived", label: "Archived" },
];

const WINDOW_OPTIONS: Array<{ value: WindowKind | ""; label: string }> = [
  { value: "", label: "Any" },
  { value: "all_time", label: "all_time" },
  { value: "recurring", label: "recurring" },
  { value: "event", label: "event" },
  { value: "recurring_event", label: "recurring_event" },
];

/** Default filter matches the mockup state 1: status = active. */
const DEFAULT_STATUS: LeaderboardStatus | "" = "active";

export function LeaderboardsListPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state lives in React; the URL is a one-way mirror so refresh +
  // shareable links keep the active filter. Initialising from the URL on mount
  // means a deep-linked `/leaderboards?status=paused&q=foo` is honoured.
  const [status, setStatus] = useState<LeaderboardStatus | "">(
    () =>
      (searchParams.get("status") as LeaderboardStatus | null) ??
      DEFAULT_STATUS,
  );
  const [windowKind, setWindowKind] = useState<WindowKind | "">(
    () => (searchParams.get("window_kind") as WindowKind | null) ?? "",
  );
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [debouncedQ, setDebouncedQ] = useState(q);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  // Single URL-sync effect — local state is the source of truth, the URL is a
  // derived projection. Using the functional setter avoids depending on the
  // current `searchParams` snapshot and removes the need for an exhaustive-deps
  // override. Foreign params (e.g. ?tab=…) are preserved across writes.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedQ) next.set("q", debouncedQ);
        else next.delete("q");
        if (status) next.set("status", status);
        else next.delete("status");
        if (windowKind) next.set("window_kind", windowKind);
        else next.delete("window_kind");
        return next;
      },
      { replace: true },
    );
  }, [debouncedQ, status, windowKind, setSearchParams]);

  const filter: ListFilter = useMemo(
    () => ({ status, window_kind: windowKind }),
    [status, windowKind],
  );

  const query = useQuery({
    queryKey: lbKeys.list(id, filter),
    queryFn: () => listLeaderboards(id, filter),
    enabled: !!id,
  });

  // Project name for the breadcrumb (same pattern as PlayersListPage).
  const { data: project } = useProject(id);

  // === Filter helpers ================================================ //

  function clearAll() {
    setStatus(DEFAULT_STATUS);
    setWindowKind("");
    setQ("");
  }

  // === Client-side q filter ========================================== //

  // Memoised for its identity, not its cost: while the query has no data the
  // `?? []` fallback is a fresh array every render, and this list is a
  // dependency of the filter below.
  const allItems: Leaderboard[] = useMemo(() => query.data ?? [], [query.data]);
  const items = useMemo(() => {
    const needle = debouncedQ.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter(
      (lb) =>
        lb.ext_key.toLowerCase().includes(needle) ||
        lb.name.toLowerCase().includes(needle),
    );
  }, [allItems, debouncedQ]);

  // === "Are any filters currently narrowing the list?" =============== //
  //
  // The empty heuristic — "No leaderboards yet" appears only when the user has
  // NOT narrowed the list. Default filter (status=active, no window, no q) is
  // treated as the baseline; if the backend returns zero rows with those
  // defaults, it's a brand-new project. Anything else → "No matches".
  const filtersActive =
    status !== DEFAULT_STATUS || windowKind !== "" || debouncedQ !== "";

  const hasActiveFilterChips = filtersActive;

  // === Header ======================================================== //

  // Breadcrumb intentionally omits a trailing "Leaderboards" crumb — the
  // PageHeader title already names the page, and a duplicate "Leaderboards"
  // node would also break the test's `getByText("Leaderboards")` assertion.
  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        {
          label: project?.name ?? "Project",
          to: `/projects/${id}`,
        },
      ]}
      title="Leaderboards"
      actions={
        <Link
          to={`/projects/${id}/leaderboards/new`}
          className={styles.createBtn}
        >
          Create leaderboard
        </Link>
      }
    />
  );

  // === Error state =================================================== //
  if (query.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <Toolbar
            q={q}
            onQ={setQ}
            status={status}
            onStatus={setStatus}
            window={windowKind}
            onWindow={setWindowKind}
            disabled
          />
          <div className={styles.errorCard}>
            <Alert variant="danger" title="Couldn't load leaderboards">
              {query.error instanceof ApiError
                ? describeError(query.error.code)
                : describeError("internal")}
            </Alert>
            <div className={styles.errorActions}>
              <Button variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Render ======================================================== //

  return (
    <>
      {header}
      <div className={styles.page}>
        <Toolbar
          q={q}
          onQ={setQ}
          status={status}
          onStatus={setStatus}
          window={windowKind}
          onWindow={setWindowKind}
          disabled={query.isLoading}
          rightSlot={
            !query.isLoading &&
            items.length > 0 && (
              <span className={styles.countCallout}>
                <Trophy size={11} aria-hidden="true" />
                {items.length} leaderboard{items.length === 1 ? "" : "s"}
              </span>
            )
          }
        />

        {hasActiveFilterChips && !query.isLoading && (
          <FilterChips
            status={status}
            windowKind={windowKind}
            q={debouncedQ}
            onClear={clearAll}
          />
        )}

        <div className={styles.tableWrap}>
          {query.isLoading ? (
            <LoadingTable />
          ) : items.length === 0 ? (
            filtersActive ? (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <SearchX size={26} aria-hidden="true" />
                </div>
                <h3>No leaderboards match these filters</h3>
                <p>Try clearing filters or adjusting the search query.</p>
                <button
                  type="button"
                  className={styles.clearLink}
                  onClick={clearAll}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <Trophy size={28} aria-hidden="true" />
                </div>
                <h3>No leaderboards yet</h3>
                <p>
                  Create your first leaderboard to start ranking players or
                  guilds.
                </p>
                <Link
                  to={`/projects/${id}/leaderboards/new`}
                  className={styles.createBtn}
                  style={{ marginTop: 12 }}
                >
                  Create leaderboard
                </Link>
              </div>
            )
          ) : (
            <LeaderboardsTable
              items={items}
              onRowClick={(lbId) =>
                navigate(`/projects/${id}/leaderboards/${lbId}`)
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

// === Toolbar ========================================================= //

interface ToolbarProps {
  q: string;
  onQ: (v: string) => void;
  status: LeaderboardStatus | "";
  onStatus: (v: LeaderboardStatus | "") => void;
  window: WindowKind | "";
  onWindow: (v: WindowKind | "") => void;
  disabled?: boolean;
  rightSlot?: React.ReactNode;
}

function Toolbar({
  q,
  onQ,
  status,
  onStatus,
  window: win,
  onWindow,
  disabled,
  rightSlot,
}: ToolbarProps) {
  return (
    <div className={styles.toolbar}>
      <div
        className={`${styles.searchInput} ${
          disabled ? styles.searchDisabled : ""
        }`}
      >
        <span className={styles.leading}>
          <Search size={14} aria-hidden="true" />
        </span>
        <input
          placeholder="Search by ext_key or name…"
          aria-label="Search leaderboards"
          value={q}
          disabled={disabled}
          onChange={(e) => onQ(e.target.value)}
        />
        {q.length > 0 && !disabled && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear search"
            onClick={() => onQ("")}
          >
            <X size={13} aria-hidden="true" />
          </button>
        )}
      </div>

      <div
        className={`${styles.seg} ${disabled ? styles.segDisabled : ""}`}
        role="tablist"
        aria-label="Status filter"
      >
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value || "all"}
            type="button"
            role="tab"
            aria-selected={status === opt.value}
            className={`${styles.segOpt} ${
              status === opt.value ? styles.segActive : ""
            }`}
            disabled={disabled}
            onClick={() => onStatus(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div
        className={`${styles.selectInput} ${
          disabled ? styles.selectDisabled : ""
        }`}
      >
        <span className={styles.selectLabel}>Window</span>
        <Select
          value={win}
          disabled={disabled}
          onChange={(v) => onWindow(v as WindowKind | "")}
          options={WINDOW_OPTIONS}
        />
      </div>

      <div className={styles.toolbarSpacer} />
      {rightSlot}
    </div>
  );
}

// === Filter chips ==================================================== //

interface FilterChipsProps {
  status: LeaderboardStatus | "";
  windowKind: WindowKind | "";
  q: string;
  onClear: () => void;
}

function FilterChips({ status, windowKind, q, onClear }: FilterChipsProps) {
  const chips: string[] = [];
  if (status && status !== DEFAULT_STATUS) {
    chips.push(STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status);
  } else if (status === DEFAULT_STATUS) {
    chips.push("Active");
  }
  if (windowKind) chips.push(windowKind);
  return (
    <div className={styles.filterChips}>
      <span className={styles.filterLabel}>Filters:</span>
      {chips.map((c) => (
        <span key={c} className={styles.chip}>
          {c}
        </span>
      ))}
      {q && (
        <span className={styles.chip}>
          <code className={styles.chipCode}>&quot;{q}&quot;</code>
        </span>
      )}
      <button type="button" className={styles.clearLinkInline} onClick={onClear}>
        Clear
      </button>
    </div>
  );
}

// === Table =========================================================== //

// Single source of truth for table headers — shared between the populated
// table and the loading skeleton so column widths stay aligned.
function TableHead() {
  return (
    <thead>
      <tr>
        <th style={{ width: "14%" }}>Ext key</th>
        <th style={{ width: "22%" }}>Name</th>
        <th>Window</th>
        <th>Operator</th>
        <th>Scope</th>
        <th className={styles.num}>Participants</th>
        <th>Status</th>
        <th>Updated</th>
        <th aria-hidden="true" style={{ width: 36 }} />
      </tr>
    </thead>
  );
}

interface TableProps {
  items: Leaderboard[];
  onRowClick: (lbId: string) => void;
}

function LeaderboardsTable({ items, onRowClick }: TableProps) {
  return (
    <table className={styles.table}>
      <TableHead />
      <tbody>
        {items.map((lb) => {
          const wb = windowBadgeFor(lb);
          const op = operatorBadgeFor(lb);
          return (
            <tr
              key={lb.id}
              role="link"
              tabIndex={0}
              aria-label={`Open ${lb.name}`}
              onClick={() => onRowClick(lb.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(lb.id);
                }
              }}
            >
              <td className={styles.extKey}>{lb.ext_key}</td>
              <td>
                <div className={styles.nameCell}>
                  <span className={styles.nameMain}>{lb.name}</span>
                  {lb.description && (
                    <span className={styles.nameDesc}>{lb.description}</span>
                  )}
                </div>
              </td>
              <td>
                <span
                  className={`${styles.badge} ${
                    wb.variant === "info"
                      ? styles.badgeInfo
                      : wb.variant === "warn"
                        ? styles.badgeWarn
                        : styles.badgeNeutral
                  }`}
                >
                  {wb.text}
                </span>
              </td>
              <td>
                <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                  <span className={styles.glyph}>{op.glyph}</span> {op.label}
                </span>
              </td>
              <td>
                <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                  {scopeLabelFor(lb)}
                </span>
              </td>
              <td className={styles.num}>
                <span className={styles.participants}>—</span>
              </td>
              <td>
                <span
                  className={`${styles.statusPill} ${
                    lb.status === "active"
                      ? styles.statusActive
                      : lb.status === "paused"
                        ? styles.statusPaused
                        : styles.statusArchived
                  }`}
                >
                  <span className={styles.statusDot} aria-hidden="true" />
                  {lb.status}
                </span>
              </td>
              <td className={styles.muted}>
                {formatRelativeShort(lb.updated_at)}
              </td>
              <td
                className={styles.kebabCell}
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className={styles.kebab}
                  aria-hidden="true"
                  title="Row actions (coming soon)"
                >
                  <MoreVertical size={14} />
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// === Loading skeleton ================================================ //

function LoadingTable() {
  return (
    <table className={styles.table}>
      <TableHead />
      <tbody>
        {Array.from({ length: 5 }).map((_, i) => (
          <tr key={i}>
            <td>
              <Skeleton width={110} height={12} />
            </td>
            <td>
              <div className={styles.skelLines}>
                <Skeleton width={160} height={12} />
                <Skeleton width={120} height={9} />
              </div>
            </td>
            <td>
              <Skeleton width={80} height={12} />
            </td>
            <td>
              <Skeleton width={70} height={12} />
            </td>
            <td>
              <Skeleton width={80} height={12} />
            </td>
            <td className={styles.num}>
              <Skeleton width={48} height={12} style={{ marginLeft: "auto" }} />
            </td>
            <td>
              <Skeleton width={60} height={12} />
            </td>
            <td>
              <Skeleton width={64} height={12} />
            </td>
            <td>
              <Skeleton width={14} height={14} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
